import React, { useEffect, useRef, useState } from "react";
import { dateTimeLong } from "@/lib/dates";
import { useConfirm } from "@/hooks/useConfirm";
import ConfirmDialog from "@/components/ConfirmDialog";
import { X, Send, Hash, ExternalLink, CheckCircle2, Bot, RotateCcw, FileCode, GitCommit, FileText, Paperclip, Pencil, Trash2, Check, Play, Loader2 } from "lucide-react";
import { api, uploadFile } from "@/lib/api";
import { useFileUrl } from "@/lib/fileAccess";
import { toast } from "sonner";
import Markdown from "@/components/Markdown";
import Avatar from "@/components/Avatar";
import CodeBlock from "@/components/CodeBlock";
import { statusLabel, priorityLabel, categoryLabel, appLabel } from "@/lib/labels";
import useFeedbackApps from "@/hooks/useFeedbackApps";
import FeedbackBadge, { AWAITING_CONFIRMATION,
  CategoryBadge, PriorityBadge, StatusBadge,
  CATEGORIES, PRIORITIES, STATUSES,
} from "@/components/FeedbackBadge";

// Agent-feedback status → CSS token + human label (colors live in index.css).
const AGENT_STATUS = {
  fix_deployed: { varName: "--agent-fix-deployed", label: "Reparație livrată" },
  partial_fix:  { varName: "--agent-partial-fix", label: "Reparație parțială" },
  needs_review: { varName: "--agent-needs-review", label: "Are nevoie de verificare" },
  cannot_fix:   { varName: "--agent-cannot-fix", label: "Nu poate fi reparat" },
};

// A Claude Code reply carries a structured `agent` payload; render it richly.
function AgentReply({ reply, actions, editor }) {
  const a = reply.agent || {};
  const meta = AGENT_STATUS[a.status] || { varName: null, label: a.status };
  return (
    <div className="agent-reply p-2.5 group" data-testid={`agent-reply-${reply.id}`}>
      <div className="flex items-center gap-2 mb-1.5 flex-wrap">
        <Bot className="w-3.5 h-3.5 text-brand-2" />
        <span className="text-xs font-semibold">{reply.user_name || "Claude Code"}</span>
        <FeedbackBadge varName={meta.varName} label={meta.label} />
        {a.env && <span className="text-[10px] mono text-fg/40 uppercase">{a.env}</span>}
        <EditedMark at={reply.edited_at} />
        {reply.source === "agent" && (
          <span className="text-[10px] mono text-fg/40 ml-auto" title="Reparat prin CI">🤖 via CI · {timeStr(reply.created_at)}</span>
        )}
        {actions}
      </div>
      {editor}
      {!editor && a.summary && <div className="text-sm text-fg/90 mb-2"><Markdown>{a.summary}</Markdown></div>}
      {!editor && a.files_modified?.length > 0 && (
        <div className="mb-2">
          <span className="label-tiny block mb-1">Fișiere modificate</span>
          <ul className="space-y-0.5">
            {a.files_modified.map((f) => (
              <li key={f} className="flex items-center gap-1.5 text-[11px] mono text-fg/70">
                <FileCode className="w-3 h-3 text-fg/40 flex-shrink-0" />
                <span className="truncate">{f}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {!editor && a.commit_hash && a.branch && (
        <p className="text-[11px] mono text-fg/60 mb-2 flex items-center gap-1.5">
          <GitCommit className="w-3 h-3 text-fg/40 flex-shrink-0" />
          <span><span className="text-brand-2">{a.commit_hash}</span> pe branch <span className="text-brand-2">{a.branch}</span></span>
        </p>
      )}
      {!editor && a.git_diff ? (
        <CodeBlock lang="diff" code={a.git_diff} compact />
      ) : null}
    </div>
  );
}

// A reply reworded after the fact says so — the thread is client-facing, so a
// silent rewrite would be worse than the original wording.
function EditedMark({ at }) {
  if (!at) return null;
  return <span className="text-[10px] text-fg/40 italic" title={`Editat ${timeStr(at)}`}>(editat)</span>;
}

// Edit / delete affordances, shown only to the reply's author and the workspace owner.
function ReplyActions({ onEdit, onDelete, replyId }) {
  return (
    <span className="ml-auto flex items-center gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
      <button type="button" onClick={onEdit} title="Editează răspunsul" aria-label="Editează răspunsul"
        data-testid={`reply-edit-${replyId}`}
        className="text-fg/40 hover:text-fg p-0.5"><Pencil className="w-3 h-3" /></button>
      <button type="button" onClick={onDelete} title="Șterge răspunsul" aria-label="Șterge răspunsul"
        data-testid={`reply-delete-${replyId}`}
        className="text-fg/40 hover:text-red-400 p-0.5"><Trash2 className="w-3 h-3" /></button>
    </span>
  );
}

function ReplyEditor({ value, onChange, onSave, onCancel, busy, replyId }) {
  return (
    <div className="mt-1">
      <textarea
        data-testid={`reply-edit-input-${replyId}`}
        value={value} onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape") { e.preventDefault(); onCancel(); }
          if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); onSave(); }
        }}
        rows={3} autoFocus
        className="input-flat resize-none text-sm w-full"
      />
      <div className="flex items-center gap-2 mt-1">
        <button type="button" onClick={onSave} disabled={busy || !value.trim()}
          data-testid={`reply-edit-save-${replyId}`}
          className="btn-primary text-xs px-2 py-1 disabled:opacity-50 flex items-center gap-1">
          <Check className="w-3 h-3" /> Salvează
        </button>
        <button type="button" onClick={onCancel} className="text-xs text-fg/50 hover:text-fg">Anulează</button>
      </div>
    </div>
  );
}

function timeStr(iso) {
  try { return dateTimeLong(iso); }
  catch { return iso; }
}

// Module-level so it isn't remounted on every parent render (would drop input focus).
const Field = ({ label, children }) => (
  <div className="flex items-center gap-2 text-xs">
    <span className="label-tiny w-20 flex-shrink-0">{label}</span>
    <div className="flex-1 min-w-0">{children}</div>
  </div>
);

function Attachment({ fileId, removable, onRemove }) {
  const [meta, setMeta] = useState(null);
  const isImage = meta?.content_type?.startsWith("image/");

  useEffect(() => {
    let active = true;
    api.get(`/files/${fileId}/metadata`)
      .then(({ data }) => { if (active) setMeta(data); })
      .catch(() => { if (active) setMeta({ name: "Attachment", content_type: "" }); });
    return () => { active = false; };
  }, [fileId]);

  const url = useFileUrl(fileId);

  return (
    <div className="relative border border-line/10 hover:border-line/30 overflow-hidden bg-surface-2 group">
      <a href={url || undefined} target="_blank" rel="noreferrer" className="block" title={meta?.name || "Deschide atașamentul"}>
        {isImage && url ? (
          <img src={url} alt={meta?.name || "attachment"} className="w-full h-24 object-cover" />
        ) : (
          <div className="h-24 flex flex-col items-center justify-center gap-2 px-3 text-fg/60">
            <FileText className="w-7 h-7 text-brand" />
            <span className="text-[11px] text-center truncate w-full">{meta?.name || "Se încarcă…"}</span>
          </div>
        )}
      </a>
      {isImage && (
        <div className="px-2 py-1 text-[10px] truncate text-fg/60" title={meta?.name}>{meta?.name}</div>
      )}
      {removable && (
        <button
          type="button"
          onClick={() => onRemove(fileId)}
          className="absolute top-1 right-1 w-6 h-6 flex items-center justify-center bg-bg/90 border border-line/20 text-fg/60 hover:text-red-400"
          aria-label={`Elimină ${meta?.name || "atașamentul"}`}
          title="Scoate din tichet"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}

export default function FeedbackDetail({ ticket, currentUser, users, channels = [], isOwner, nameOf, onClose, onUpsert, onGoToChannel }) {
  const { ask, confirmProps } = useConfirm();
  const isReporter = ticket.reporter_id === currentUser.id;
  const canEditContent = isReporter || isOwner;
  const members = [currentUser, ...users];
  // Agent feedback auto-assigns "bot:<token-name>"; render that distinctly.
  const assigneeIsBot = (ticket.assignee_id || "").startsWith("bot:");
  const assigneeBotName = assigneeIsBot ? ticket.assignee_id.slice(4) : null;

  // Back-nav target: the originating channel, else fall back to #deployments.
  const deploymentsChannel = channels.find((c) => c.name === "deployments");
  const backChannelId = ticket.channel_id || deploymentsChannel?.id;
  const backChannelName = ticket.channel_id
    ? (channels.find((c) => c.id === ticket.channel_id)?.name || "channel")
    : "deployments";

  const [editingDesc, setEditingDesc] = useState(false);
  const [descDraft, setDescDraft] = useState(ticket.description || "");
  const [titleDraft, setTitleDraft] = useState(ticket.title);
  const [showCustom, setShowCustom] = useState(false);
  const [customStatus, setCustomStatus] = useState(ticket.custom_status || "");
  const [reply, setReply] = useState("");
  const [project, setProject] = useState(null);
  const [agentRun, setAgentRun] = useState(null);
  const [editingReplyId, setEditingReplyId] = useState(null);
  const [replyDraft, setReplyDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [uploadingAttachments, setUploadingAttachments] = useState(false);
  const attachmentInput = useRef(null);

  // Valoarea purtată de tichet apare întotdeauna în meniu, altfel ar dispărea
  // de pe ecran cât timp lista se încarcă. Marcajul „nu mai e în listă" se pune
  // abia după ce lista chiar a venit — până atunci n-avem cu ce s-o comparăm.
  const { apps: appOptions, loaded: appsLoaded } = useFeedbackApps(ticket.workspace_id);
  const appOutOfList = Boolean(ticket.app) && !appOptions.includes(ticket.app);

  // Claude Code can only be launched for a project bound to a checkout on this
  // server; the server decides that (can_run_agent) and the button follows it.
  useEffect(() => {
    let active = true;
    if (!ticket.project_id) { setProject(null); return undefined; }
    api.get("/ticket-projects", { params: { workspace_id: ticket.workspace_id } })
      .then(({ data }) => {
        if (active) setProject((data || []).find((p) => p.id === ticket.project_id) || null);
      })
      .catch(() => { if (active) setProject(null); });
    return () => { active = false; };
  }, [ticket.project_id, ticket.workspace_id]);

  useEffect(() => {
    let active = true;
    api.get(`/feedback/${ticket.id}/agent-run`)
      .then(({ data }) => { if (active) setAgentRun(data && data.id ? data : null); })
      .catch(() => { if (active) setAgentRun(null); });
    return () => { active = false; };
  }, [ticket.id, ticket.replies?.length]);

  const agentRunning = agentRun?.status === "pending" || agentRun?.status === "publishing";
  const canRunAgent = isOwner && project?.can_run_agent && !agentRunning;

  const runAgent = () => ask({
    title: `Pornești Claude Code pe «${project.name}»?`, confirmLabel: "Pornește",
    description: `Lucrează în ${project.repo_path} ca utilizator neprivilegiat, editează `
      + "fișiere, dar nu comite și nu face push. Rezultatul apare ca reply pe ticket.",
    testId: "confirm-run-agent", onConfirm: doRunAgent,
  });

  const doRunAgent = async () => {
    setBusy(true);
    try {
      // Uses the client default (see DEFAULT_TIMEOUT_MS): starting a run is a
      // fast call, the work happens on the host runner afterwards.
      const { data } = await api.post(`/feedback/${ticket.id}/agent-run`);
      setAgentRun({ id: data.job_id, status: "pending", project_slug: data.project });
      toast.success("Claude Code a pornit — rezultatul apare aici când termină.");
    } catch (e) {
      // A timeout on a POST that is not idempotent tells us nothing about
      // whether the run started, so neither retry nor guess: ask the server.
      if (e?.code === "ECONNABORTED") { await reconcileAgentRun(); return; }
      toast.error(e?.response?.data?.detail || "Nu s-a putut porni");
    } finally { setBusy(false); }
  };

  // Called when starting a run timed out. GET /agent-run is the authority on
  // whether one is actually in flight; the button follows what it says.
  const reconcileAgentRun = async () => {
    try {
      const { data } = await api.get(`/feedback/${ticket.id}/agent-run`);
      if (data && data.id) {
        setAgentRun(data);
        toast.success(data.status === "pending" || data.status === "publishing"
          ? "Serverul a răspuns târziu, dar rularea a pornit — rezultatul apare aici."
          : `Serverul a răspuns târziu. Ultima rulare: ${data.status}.`);
      } else {
        setAgentRun(null);
        toast.error("Serverul nu a răspuns la timp și nicio rulare nu a pornit. Încearcă din nou.");
      }
    } catch {
      toast.error("Serverul nu a răspuns la timp și nu pot verifica starea rulării. "
                  + "Reîncarcă tichetul înainte să apeși din nou.");
    }
  };

  // An agent-deployed fix waiting for the owner to sign off.
  const awaitingConfirm = ticket.status === AWAITING_CONFIRMATION;

  const doConfirmFix = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/feedback/${ticket.id}/confirm`);
      onUpsert(data);
      setConfirmOpen(false);
      toast.success("Fix confirmat — tichet Rezolvat");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Confirmarea nu a reușit");
    } finally { setBusy(false); }
  };

  const reopen = () => ask({
    title: "Redeschizi ticketul?", confirmLabel: "Redeschide",
    description: "Fix-ul nu rezolvă problema, iar ticketul revine în lucru.",
    testId: "confirm-reopen", onConfirm: doReopen,
  });

  const doReopen = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/feedback/${ticket.id}`, { status: "Reopened", custom_status: null });
      onUpsert(data);
      toast.success("Ticket redeschis");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Redeschiderea nu a reușit");
    } finally { setBusy(false); }
  };

  // Statuses this user may set via the enum dropdown (Custom is always allowed).
  const statusOptions = isOwner
    ? STATUSES
    : (isReporter && ticket.status !== "Closed" ? ["Resolved"] : []);

  const patch = async (fields) => {
    try {
      const { data } = await api.patch(`/feedback/${ticket.id}`, fields);
      onUpsert(data);
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Actualizarea nu a reușit");
      return false;
    }
  };

  const removeAttachment = async (fileId) => {
    const next = (ticket.screenshots || []).filter((id) => id !== fileId);
    await patch({ screenshots: next });
  };

  const addAttachments = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploadingAttachments(true);
    try {
      const uploaded = [];
      for (const file of files) uploaded.push(await uploadFile(ticket.workspace_id, file));
      const next = [...(ticket.screenshots || []), ...uploaded.map((file) => file.id)];
      if (await patch({ screenshots: next })) toast.success(`${uploaded.length} ${uploaded.length === 1 ? "fișier atașat" : "fișiere atașate"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Încărcarea nu a reușit");
    } finally {
      setUploadingAttachments(false);
    }
  };

  const onStatusSelect = (e) => {
    const v = e.target.value;
    e.target.value = "";
    if (!v) return;
    if (v === "__custom") { setShowCustom(true); return; }
    patch({ status: v, custom_status: null });
  };

  const saveTitle = async () => {
    const v = titleDraft.trim();
    if (!v || v === ticket.title) { setTitleDraft(ticket.title); return; }
    if (!(await patch({ title: v }))) setTitleDraft(ticket.title);
  };

  const saveDesc = async () => {
    if (await patch({ description: descDraft })) setEditingDesc(false);
  };

  const sendReply = async () => {
    const content = reply.trim();
    if (!content) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/feedback/${ticket.id}/reply`, { content });
      // Optimistic: reflect immediately (the WS feedback_reply event dedups).
      onUpsert({ ...ticket, replies: [...(ticket.replies || []), data] });
      setReply("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Răspunsul nu a plecat");
    } finally { setBusy(false); }
  };

  // A reply is editable by whoever wrote it, and by the workspace owner. Agent
  // replies are authored by "bot:<id>", so only the owner can touch those here.
  const canEditReply = (r) => isOwner || r.user_id === currentUser.id;

  const startEditReply = (r) => {
    setEditingReplyId(r.id);
    // Agent replies render (and are edited) as their summary, not the Markdown blob.
    setReplyDraft(r.agent ? (r.agent.summary || "") : (r.content || ""));
  };

  const saveEditReply = async (r) => {
    const content = replyDraft.trim();
    if (!content) return;
    setBusy(true);
    try {
      const { data } = await api.patch(`/feedback/${ticket.id}/replies/${r.id}`, { content });
      onUpsert({ ...ticket, replies: (ticket.replies || []).map((x) => x.id === r.id ? data : x) });
      setEditingReplyId(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Editarea nu a reușit");
    } finally { setBusy(false); }
  };

  const deleteReply = (r) => ask({
    title: "Ștergi replica?", destructive: true, confirmLabel: "Șterge",
    description: "Dispare pentru toată lumea de pe ticket. Nu se poate anula.",
    testId: "confirm-delete-reply", onConfirm: () => doDeleteReply(r),
  });

  const doDeleteReply = async (r) => {
    setBusy(true);
    try {
      await api.delete(`/feedback/${ticket.id}/replies/${r.id}`);
      onUpsert({ ...ticket, replies: (ticket.replies || []).filter((x) => x.id !== r.id) });
      if (editingReplyId === r.id) setEditingReplyId(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Ștergerea nu a reușit");
    } finally { setBusy(false); }
  };

  return (
    <div className="w-[420px] flex flex-col min-h-0 border-l border-line/10 bg-bg" data-testid="feedback-detail">
      <div className="h-14 px-4 flex items-center gap-2 border-b border-line/10">
        <div className="flex items-center gap-1.5 flex-wrap flex-1 min-w-0">
          <CategoryBadge value={ticket.category} />
          <PriorityBadge value={ticket.priority} />
          <span key={ticket.custom_status || ticket.status} className="fade-in inline-flex"><StatusBadge value={ticket.status} custom={ticket.custom_status} /></span>
        </div>
        <button onClick={onClose} className="text-fg/50 hover:text-fg" data-testid="feedback-detail-close"><X className="w-4 h-4" /></button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Claude Code, for a project bound to a checkout on this server */}
        {(canRunAgent || agentRunning) && (
          <div className="agent-reply p-3" data-testid="agent-run-panel">
            <div className="flex items-center gap-2 mb-1.5">
              <Bot className="w-4 h-4 text-brand-2" />
              <span className="text-sm font-semibold">Claude Code</span>
              {project?.slug && <code className="text-[10px] mono text-fg/50">{project.slug}</code>}
            </div>
            {agentRunning ? (
              <p className="text-xs text-fg/70 flex items-center gap-1.5" data-testid="agent-run-active">
                <Loader2 className="w-3.5 h-3.5 animate-spin text-brand-2" />
                Rulează pe server… rezultatul apare aici ca reply.
              </p>
            ) : (
              <>
                <p className="text-xs text-fg/70 mb-2.5">
                  Pornește agentul în <code className="mono text-brand-2">{project?.repo_path}</code>.
                  Editează fișiere, dar nu comite și nu face push.
                </p>
                <button
                  data-testid="agent-run-start"
                  onClick={runAgent} disabled={busy}
                  className="btn-primary text-xs flex items-center gap-1.5 px-3 py-1.5 disabled:opacity-50"
                >
                  {busy ? (
                    <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Se pornește…</>
                  ) : (
                    <><Play className="w-3.5 h-3.5" /> Fix cu Claude Code</>
                  )}
                </button>
              </>
            )}
          </div>
        )}

        {/* Agent fix awaiting owner confirmation */}
        {awaitingConfirm && (
          <div className="agent-reply p-3" data-testid="confirm-banner">
            <div className="flex items-center gap-2 mb-1.5">
              <Bot className="w-4 h-4 text-brand-2" />
              <span className="text-sm font-semibold">🤖 Claude Code a terminat fix-ul</span>
            </div>
            <p className="text-xs text-fg/70 mb-2.5">Verifică și confirmă dacă fix-ul rezolvă bug-ul raportat.</p>
            {isOwner ? (
              <div className="flex items-center gap-2">
                <button
                  data-testid="banner-confirm-fix"
                  onClick={() => setConfirmOpen(true)} disabled={busy}
                  className="btn-primary text-xs flex items-center gap-1.5 px-3 py-1.5 disabled:opacity-50"
                >
                  <CheckCircle2 className="w-3.5 h-3.5" /> Confirmă reparația
                </button>
                <button
                  data-testid="banner-reopen"
                  onClick={reopen} disabled={busy}
                  className="btn-ghost text-xs flex items-center gap-1.5 px-3 py-1.5 disabled:opacity-50"
                >
                  <RotateCcw className="w-3.5 h-3.5" /> Redeschide
                </button>
              </div>
            ) : (
              <p className="text-[11px] text-fg/40 italic">Doar proprietarul workspace-ului poate confirma reparația.</p>
            )}
          </div>
        )}

        {/* Title */}
        {canEditContent ? (
          <input
            data-testid="detail-title"
            value={titleDraft} maxLength={200}
            onChange={(e) => setTitleDraft(e.target.value)}
            onBlur={saveTitle}
            onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
            className="w-full bg-transparent text-base font-bold tracking-tight outline-none border-b border-transparent focus:border-line/30"
          />
        ) : (
          <h2 className="text-base font-bold tracking-tight break-words">{ticket.title}</h2>
        )}

        {/* Status changer */}
        <div className="space-y-1.5">
          <select data-testid="status-changer" onChange={onStatusSelect} defaultValue="" className="input-flat text-xs py-1">
            <option value="" disabled>Schimbă starea…</option>
            {statusOptions.map((s) => <option key={s} value={s}>{statusLabel(s)}</option>)}
            <option value="__custom">Altul…</option>
          </select>
          {showCustom && (
            <div className="flex items-center gap-2">
              <input value={customStatus} onChange={(e) => setCustomStatus(e.target.value)} placeholder="Stare proprie" className="input-flat text-xs py-1" />
              <button onClick={async () => { if (await patch({ custom_status: customStatus.trim() || null })) { setCustomStatus(""); setShowCustom(false); } }} className="btn-primary text-xs px-2 py-1">Setează</button>
            </div>
          )}
        </div>

        {/* Meta */}
        <div className="space-y-2 border-t border-line/10 pt-3">
          <Field label="Aplicație">
            <select
              data-testid="detail-app"
              value={ticket.app || ""}
              onChange={(e) => { if (e.target.value !== ticket.app) patch({ app: e.target.value }); }}
              className="input-flat text-xs py-1"
            >
              {/* Un tichet vechi poate purta o aplicație scoasă din registru.
                  Rămâne aici, marcată, ca să nu se rescrie de la sine când
                  cineva schimbă altceva pe tichet. */}
              {appOutOfList && <option value={ticket.app}>{appLabel(ticket.app, !appsLoaded)}</option>}
              {appOptions.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </Field>
          <Field label="Categorie">
            <select value={ticket.category} disabled={!canEditContent} onChange={(e) => patch({ category: e.target.value })} className="input-flat text-xs py-1 disabled:opacity-60">
              {CATEGORIES.map((c) => <option key={c} value={c}>{categoryLabel(c)}</option>)}
            </select>
          </Field>
          <Field label="Prioritate">
            <select value={ticket.priority} onChange={(e) => patch({ priority: e.target.value })} className="input-flat text-xs py-1">
              {PRIORITIES.map((p) => <option key={p} value={p}>{priorityLabel(p)}</option>)}
            </select>
          </Field>
          <Field label="Responsabil">
            <div className="flex items-center gap-1.5">
              {assigneeIsBot ? (
                <span className="flex-shrink-0 text-sm" title={`Atribuit de Claude Code (${assigneeBotName})`}>🤖</span>
              ) : (
                ticket.assignee_id && <Avatar user={{ id: ticket.assignee_id, name: nameOf(ticket.assignee_id) }} size={18} />
              )}
              <select value={ticket.assignee_id || ""} onChange={(e) => patch({ assignee_id: e.target.value || null })} className="input-flat text-xs py-1 flex-1">
                <option value="">Nealocat</option>
                {assigneeIsBot && <option value={ticket.assignee_id} disabled>🤖 {assigneeBotName}</option>}
                {members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </div>
          </Field>
          <Field label="Raportor">
            <span className="flex items-center gap-1.5 text-fg/70">
              <Avatar user={{ id: ticket.reporter_id, name: nameOf(ticket.reporter_id) }} size={18} /> {nameOf(ticket.reporter_id)}
            </span>
          </Field>
          <Field label="Creat"><span className="text-fg/50 mono">{timeStr(ticket.created_at)}</span></Field>
        </div>

        {/* Description */}
        <div className="border-t border-line/10 pt-3">
          <div className="flex items-center justify-between mb-1.5">
            <span className="label-tiny">Descriere</span>
            {canEditContent && !editingDesc && (
              <button onClick={() => { setDescDraft(ticket.description || ""); setEditingDesc(true); }} className="text-[11px] text-brand hover:text-brand-2">editat</button>
            )}
          </div>
          {editingDesc ? (
            <div className="space-y-1.5">
              <textarea value={descDraft} onChange={(e) => setDescDraft(e.target.value)} rows={4} className="input-flat resize-none text-sm" />
              <div className="flex gap-2">
                <button onClick={saveDesc} className="btn-primary text-xs px-2 py-0.5">Salvează</button>
                <button onClick={() => setEditingDesc(false)} className="text-xs text-fg/50 hover:text-fg">Anulează</button>
              </div>
            </div>
          ) : (
            ticket.description
              ? <div className="text-sm text-fg/90"><Markdown>{ticket.description}</Markdown></div>
              : <p className="text-xs text-fg/40 italic">Fără descriere.</p>
          )}
        </div>

        {/* Attachments (legacy API field: screenshots) */}
        {(ticket.screenshots?.length > 0 || canEditContent) && (
          <div className="border-t border-line/10 pt-3">
            <div className="flex items-center justify-between mb-2">
              <span className="label-tiny">Atașamente</span>
              {canEditContent && (
                <>
                  <button
                    type="button"
                    onClick={() => attachmentInput.current?.click()}
                    disabled={uploadingAttachments}
                    className="inline-flex items-center gap-1 text-[11px] text-brand hover:text-brand-2 disabled:opacity-50"
                    data-testid="detail-add-attachments"
                  >
                    <Paperclip className="w-3 h-3" /> {uploadingAttachments ? "se încarcă…" : "adaugă fișiere"}
                  </button>
                  <input ref={attachmentInput} type="file" multiple onChange={addAttachments} className="hidden" />
                </>
              )}
            </div>
            {ticket.screenshots?.length > 0 ? (
            <div className="grid grid-cols-2 gap-2">
              {ticket.screenshots.map((sid) => (
                <Attachment key={sid} fileId={sid} removable={canEditContent} onRemove={removeAttachment} />
              ))}
            </div>
            ) : (
              <button
                type="button"
                onClick={() => attachmentInput.current?.click()}
                disabled={uploadingAttachments}
                className="w-full border border-dashed border-line/20 py-4 text-xs text-fg/40 hover:text-fg/70 hover:border-line/40 disabled:opacity-50"
              >
                <Paperclip className="w-4 h-4 mx-auto mb-1" />
                Adaugă imagini sau documente
              </button>
            )}
          </div>
        )}

        {/* Replies */}
        <div className="border-t border-line/10 pt-3">
          <span className="label-tiny block mb-2">Răspunsuri ({ticket.replies?.length || 0})</span>
          <div className="space-y-2">
            {(ticket.replies || []).map((r) => {
              const editing = editingReplyId === r.id;
              const actions = canEditReply(r) && !editing ? (
                <ReplyActions replyId={r.id} onEdit={() => startEditReply(r)} onDelete={() => deleteReply(r)} />
              ) : null;
              const editor = editing ? (
                <ReplyEditor
                  replyId={r.id} value={replyDraft} onChange={setReplyDraft} busy={busy}
                  onSave={() => saveEditReply(r)} onCancel={() => setEditingReplyId(null)}
                />
              ) : null;
              return r.agent ? (
                <AgentReply key={r.id} reply={r} actions={actions} editor={editor} />
              ) : (
                <div key={r.id} className="bg-surface-2 border border-line/10 p-2 group">
                  <div className="flex items-baseline gap-2 mb-0.5">
                    <span className="text-xs font-semibold">{r.user_name || nameOf(r.user_id)}</span>
                    <span className="text-[10px] text-fg/40 mono">{timeStr(r.created_at)}</span>
                    <EditedMark at={r.edited_at} />
                    {actions}
                  </div>
                  {editor || <div className="text-sm text-fg/90"><Markdown>{r.content}</Markdown></div>}
                </div>
              );
            })}
          </div>
          <div className="mt-2 flex items-end gap-2">
            <textarea
              data-testid="reply-input"
              value={reply} onChange={(e) => setReply(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendReply(); } }}
              rows={2} placeholder="Scrie un răspuns…"
              className="input-flat resize-none text-sm flex-1"
            />
            <button data-testid="reply-send" onClick={sendReply} disabled={busy || !reply.trim()} className="btn-primary text-sm px-3 py-1.5 disabled:opacity-50">
              <Send className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>

      {backChannelId && onGoToChannel && (
        <div className="px-4 py-2 border-t border-line/10">
          <button data-testid="go-to-channel" onClick={() => onGoToChannel(backChannelId)} className="flex items-center gap-1.5 text-xs text-brand hover:text-brand-2">
            <Hash className="w-3.5 h-3.5" /> Mergi la #{backChannelName} <ExternalLink className="w-3 h-3" />
          </button>
        </div>
      )}

      {/* Confirm-fix dialog (replaces the native confirm) */}
      {confirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" onMouseDown={() => setConfirmOpen(false)} data-testid="confirm-fix-dialog">
          <div onMouseDown={(e) => e.stopPropagation()} className="w-full max-w-sm bg-surface border border-line/10 shadow-xl">
            <div className="px-4 py-3 border-b border-line/10">
              <h3 className="font-bold tracking-tight">Confirmi fix-ul?</h3>
            </div>
            <div className="p-4 space-y-4">
              <p className="text-sm text-fg/70">
                Ești sigur că fix-ul rezolvă problema raportată? Această acțiune marchează ticket-ul ca <span className="font-semibold">Rezolvat</span>.
              </p>
              <div className="flex justify-end gap-2">
                <button onClick={() => setConfirmOpen(false)} className="btn-ghost text-sm px-3 py-1.5">Anulează</button>
                <button
                  data-testid="confirm-fix-yes"
                  onClick={doConfirmFix} disabled={busy}
                  className="text-sm px-3 py-1.5 font-medium text-on-accent flex items-center gap-1.5 disabled:opacity-50"
                  style={{ background: "rgb(var(--fb-resolved))" }}
                >
                  <CheckCircle2 className="w-3.5 h-3.5" /> Da, confirmă
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <ConfirmDialog {...confirmProps} />
    </div>
  );
}
